// Frontend-only KPI catalog: metric order, i18n label keys, and subcategory label/color
// resolution. The backend owns the values/targets/status (see src/services/kpi.ts); the FE
// only maps the stable `key` strings returned by the BE to a localized label and a color.

import {
  KPI_BANK_COLORS,
  KPI_QUALIFICATION_COLORS,
  KPI_REASON_COLORS,
} from "./kpiStatus";

export type KpiMetricKey =
  | "qualifications"
  | "disbursedCount"
  | "disbursedSum"
  | "leadsInBank"
  | "approved"
  | "disqualifications";

// Display order of the metric selector (pills / dropdown).
export const KPI_METRIC_ORDER: KpiMetricKey[] = [
  "qualifications",
  "disbursedCount",
  "disbursedSum",
  "leadsInBank",
  "approved",
  "disqualifications",
];

// metric key -> i18n label key.
export const KPI_METRIC_LABEL_KEYS: Record<string, string> = {
  qualifications: "profilePage.kpi.metrics.qualifications",
  disbursedCount: "profilePage.kpi.metrics.disbursedCount",
  disbursedSum: "profilePage.kpi.metrics.disbursedSum",
  leadsInBank: "profilePage.kpi.metrics.leadsInBank",
  approved: "profilePage.kpi.metrics.approved",
  disqualifications: "profilePage.kpi.metrics.disqualifications",
};

export const getMetricLabelKey = (metric: string): string =>
  KPI_METRIC_LABEL_KEYS[metric] ?? metric;

// Subcategory key -> { i18n label key, color }. Covers sources, banks and reasons.
// The BE returns these keys verbatim in `subcategories[].key`.
interface SubcategoryMeta {
  labelKey: string;
  color: string;
}

export const KPI_SUBCATEGORY_META: Record<string, SubcategoryMeta> = {
  // qualification sources
  agent: { labelKey: "profilePage.kpi.sources.agent", color: KPI_QUALIFICATION_COLORS.agent },
  web: { labelKey: "profilePage.kpi.sources.web", color: KPI_QUALIFICATION_COLORS.web },
  app: { labelKey: "profilePage.kpi.sources.app", color: KPI_QUALIFICATION_COLORS.app },
  // banks
  bankNow: { labelKey: "profilePage.kpi.banks.bankNow", color: KPI_BANK_COLORS.bankNow },
  cembra: { labelKey: "profilePage.kpi.banks.cembra", color: KPI_BANK_COLORS.cembra },
  corner: { labelKey: "profilePage.kpi.banks.corner", color: KPI_BANK_COLORS.corner },
  // disqualification reasons
  clientCanceled: {
    labelKey: "profilePage.kpi.reasons.clientCanceled",
    color: KPI_REASON_COLORS.clientCanceled,
  },
  rejectedWk: {
    labelKey: "profilePage.kpi.reasons.rejectedWk",
    color: KPI_REASON_COLORS.rejectedWk,
  },
  rejectedBank: {
    labelKey: "profilePage.kpi.reasons.rejectedBank",
    color: KPI_REASON_COLORS.rejectedBank,
  },
};

// Fallback palette for any unmapped subcategory key the BE might add.
const FALLBACK_COLORS = ["#B046AC", "#DF86DC", "#FFC6FD", "#60B046", "#3B82C4", "#D75060"];

export const getSubcategoryLabelKey = (key: string): string =>
  KPI_SUBCATEGORY_META[key]?.labelKey ?? key;

export const getSubcategoryColor = (key: string, index = 0): string =>
  KPI_SUBCATEGORY_META[key]?.color ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
